import React from 'react';
import { Skeleton, Progress, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import {
  streamsLoadingSelector,
  streamsDataSelector,
  devicesDataSelector,
  devicesLoadingSelector,
  viewersLoadingSelector,
  viewersDataSelector,
} from '@store/overviewDashboard/widgetMessageData/selectors';
import GoodIcon from '@icons/ovb/good.svg';
import ImpactedIcon from '@icons/ovb/impacted.svg';
import { metricKeys } from '@pulse/metrics';
import {
  isIntervalGreaterThanADaySelector,
  isIntervalOlderThanThirtyDaysSelector,
} from '@store/overviewDashboard/base/selectors';
import { setSelectedBar } from '@store/overviewDashboard/impactedViewers/actions';
import { viewersOvbFeatureSelector } from '@store/common/selectors';
import {
  InfoWrapper,
  InfoBox,
  InfoText,
  InfoPct,
  InfoProgress,
  Divider,
  PctWrapper,
  IconWrap,
  StyledBaseChart,
} from './styled';

const TabSkeleton = () => (
  <InfoWrapper>
    <Skeleton
      active
      title={false}
      loading
      paragraph={{ rows: 1 }}
    />
  </InfoWrapper>
);

const ImpactScale = ({ percent }) => (
  <InfoProgress>
    <Progress strokeColor="#c32928" percent={percent} size="small" showInfo={false} />
  </InfoProgress>
);

export const DevicesTab = () => {
  const { loading } = useSelector(devicesLoadingSelector);
  const data = useSelector(devicesDataSelector);

  // do not show devices tab for more than 24h
  // switch to default metric if impacted devices is selected
  const isIntervalGreaterThanADay = useSelector(isIntervalGreaterThanADaySelector);
  if (isIntervalGreaterThanADay) {
    return (<InfoText>Impacted Devices are only supported for 24 hour range.</InfoText>);
  }

  if (loading) {
    return <TabSkeleton />;
  }

  return (
    <InfoWrapper>
      <StyledBaseChart
        key="devices-bar-chart"
        option={data}
      />
    </InfoWrapper>
  );
};

export const StreamsTab = ({ onTabMetricClick, selectedMetric }) => {
  const { loading } = useSelector(streamsLoadingSelector);
  const data = useSelector(streamsDataSelector);

  if (loading) {
    return <TabSkeleton />;
  }

  const onClick = () => {
    const metric = selectedMetric === metricKeys.BadSession
      ? metricKeys.StreamingPerformanceIndex : metricKeys.BadSession;
    onTabMetricClick(metric);
  };
  return (
    <InfoWrapper
      onClick={onClick}
      className={classnames({ selected: selectedMetric === metricKeys.BadSession }, 'streams-tab')}
    >
      <div>
        <InfoBox>{data.BadSession}</InfoBox>
        <InfoText>Impacted Streams</InfoText>
      </div>
      <ImpactScale percent={data.rawBadSessionPct} />
      <PctWrapper>
        <InfoPct>
          {data.BadSessionPct}
          {' '}
          of the Total Streams
        </InfoPct>
        {data.showPlayingTime ? (
          <InfoPct>
            <Tooltip title="Impacted stream average playing time">
              <IconWrap>
                {' '}
                <ImpactedIcon />
                {' '}
              </IconWrap>
              {data.BadSessionAverageLifePlayingTimeMins}
            </Tooltip>
            <Divider />
            <Tooltip title="Good stream average playing time">
              <IconWrap>
                {' '}
                <GoodIcon />
                {' '}
              </IconWrap>
              {data.GoodSessionAverageLifePlayingTimeMins}
            </Tooltip>
          </InfoPct>
        )
          : (
            <InfoPct>
              Engagement difference
              {' '}
              <strong>low</strong>
            </InfoPct>
          )
        }
      </PctWrapper>
    </InfoWrapper>
  );
};

export const ViewersTab = () => {
  const { loading } = useSelector(viewersLoadingSelector);
  const data = useSelector(viewersDataSelector);
  const dispatch = useDispatch();
  const viewersOvbEnabled = useSelector(viewersOvbFeatureSelector);

  // do not show viewers tab for more than 24h
  const isIntervalGreaterThanADay = useSelector(isIntervalGreaterThanADaySelector);
  const isIntervalOlderThanThirtyDays = useSelector(isIntervalOlderThanThirtyDaysSelector);

  const events: any = React.useMemo(() => {
    if (viewersOvbEnabled) {
      return [['click', 'series.bar', (e) => {
        const { data, dataIndex } = e;
        const { value } = data;
        // series is ignored, we only show bad sessions
        dispatch(setSelectedBar(value[0], dataIndex));
      }],
      ];
    }
    return [];
  }, [viewersOvbEnabled]);

  if (isIntervalGreaterThanADay || isIntervalOlderThanThirtyDays) {
    return (<InfoText>Impacted Viewers are only supported for 24 hour range within 30 days.</InfoText>);
  }

  if (loading) {
    return <TabSkeleton />;
  }

  return (
    <InfoWrapper>
      <StyledBaseChart
        key="devices-bar-chart"
        // ref={chartContainer}
        option={data}
        events={events}
      />
    </InfoWrapper>
  );
};
